import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { sendEmail } from '../services/emailService';
import { 
  Sparkles, 
  Wrench, 
  Truck, 
  Camera, 
  Shield, 
  Clock, 
  DollarSign, 
  Users, 
  CheckCircle, 
  Star,
  ArrowRight,
  Building,
  Phone,
  MapPin
} from 'lucide-react';

const ServiceProviders = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    location: '',
    serviceTypes: [],
    experience: '',
    availability: '',
    description: '',
    certifications: '',
    insurance: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show loading message
    toast.loading('Submitting your application...');
    
    try {
      // Create enhanced form data for email
      const emailFormData = {
        name: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        subject: 'Service Provider Application',
        inquiryType: 'Service Provider Partnership',
        message: `Company: ${formData.companyName}\nContact Person: ${formData.contactPerson}\nLocation: ${formData.location}\nService Types: ${formData.serviceTypes.join(', ')}\nExperience: ${formData.experience}\nAvailability: ${formData.availability}\nCertifications: ${formData.certifications}\nInsurance: ${formData.insurance ? 'Yes' : 'No'}\n\nDescription:\n${formData.description}`
      };
      
      const result = await sendEmail(emailFormData, 'Service Provider', 'Service Providers Page');
      
      if (result.success) {
        toast.success('Application submitted! We\'ll review and contact you within 48 hours.');
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          location: '',
          serviceTypes: [],
          experience: '',
          availability: '',
          description: '',
          certifications: '',
          insurance: false
        });
      } else {
        toast.error('Failed to submit application. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit application. Please try again or contact us directly.');
    }
  };

  const serviceCategories = [
    {
      icon: Sparkles,
      title: "Cleaning Services",
      description: "Professional cleaning and housekeeping for short-term rentals",
      services: ["Deep Cleaning", "Turnover Cleaning", "Maintenance Cleaning", "Laundry Services"]
    },
    {
      icon: Wrench,
      title: "Maintenance & Repairs",
      description: "Property maintenance and repair services for hospitality properties",
      services: ["Plumbing", "Electrical", "HVAC", "General Repairs", "Appliance Repair"]
    },
    {
      icon: Truck,
      title: "Logistics & Transportation",
      description: "Delivery and transportation services for guests and properties",
      services: ["Airport Transfers", "Grocery Delivery", "Key Exchange", "Supply Delivery"]
    },
    {
      icon: Camera,
      title: "Photography & Marketing",
      description: "Professional photography and marketing content creation",
      services: ["Property Photography", "Virtual Tours", "Content Creation", "Listing Optimization"]
    }
  ];

  const benefits = [
    {
      icon: DollarSign,
      title: "Guaranteed Income",
      description: "Regular work with consistent payment schedules"
    },
    {
      icon: Users,
      title: "Growing Network",
      description: "Join our network of 500+ properties across multiple markets"
    },
    {
      icon: Shield,
      title: "Insurance Coverage",
      description: "Work under our comprehensive insurance protection"
    },
    {
      icon: Clock,
      title: "Flexible Scheduling",
      description: "Choose your availability and manage your workload"
    }
  ];

  const handleServiceTypeChange = (serviceType, checked) => {
    if (checked) {
      setFormData({
        ...formData,
        serviceTypes: [...formData.serviceTypes, serviceType]
      });
    } else {
      setFormData({
        ...formData,
        serviceTypes: formData.serviceTypes.filter(type => type !== serviceType)
      });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1579705625203-9e335af5abc7)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        
        <div className="relative z-10 flex items-center h-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-6xl font-extralight text-white mb-8 leading-tight">
                Partner With Excellence
                <span className="block font-light text-white/90">Join Our Network</span>
              </h1>
              <p className="text-xl text-white/90 mb-12 font-light leading-relaxed max-w-3xl mx-auto">
                Elevate your business by joining our exclusive network of premium service providers. 
                Work with luxury properties across the USA, UAE, and Africa.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" className="bg-white text-black hover:bg-gray-100 font-light px-10 py-6">
                  <Building className="mr-3 h-5 w-5" />
                  Apply Now
                </Button>
                <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-black font-light px-10 py-6">
                  <Phone className="mr-3 h-5 w-5" />
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Service Opportunities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're looking for reliable, professional service providers across multiple categories.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {serviceCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="bg-purple-100 p-4 rounded-full">
                        <IconComponent className="h-8 w-8 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-semibold text-gray-900 mb-2">{category.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{category.description}</p>
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-gray-900 mb-3">Services Needed:</h4>
                    <div className="flex flex-wrap gap-2">
                      {category.services.map((service, idx) => (
                        <Badge key={idx} variant="outline" className="text-purple-700 border-purple-300">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Partner With Us?
            </h2>
            <p className="text-xl text-gray-600">
              Join a network that values quality, reliability, and growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="text-center hover:shadow-md transition-shadow">
                  <CardContent className="p-8">
                    <div className="bg-purple-100 p-4 rounded-full w-fit mx-auto mb-6">
                      <IconComponent className="h-8 w-8 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-6 w-6 text-yellow-300 fill-current inline-block mx-1" />
            ))}
          </div>
          <blockquote className="text-2xl font-medium mb-8 italic leading-relaxed">
            "Working with Vantage Points has transformed my cleaning business. 
            I now have steady work with premium properties and professional support."
          </blockquote>
          <div className="flex items-center justify-center space-x-4">
            <img 
              src="https://images.unsplash.com/photo-1531123414780-f74242c2b052?w=100" 
              alt="Service Provider"
              className="w-16 h-16 rounded-full object-cover"
            />
            <div className="text-left">
              <p className="font-semibold">Adaora Okafor</p>
              <p className="text-purple-200">Cleaning Services • Lagos, Africa</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ready to Join Our Network?
            </h2>
            <p className="text-xl text-gray-600">
              Complete your application to start partnering with us.
            </p>
          </div>

          <Card className="shadow-xl border-0">
            <CardHeader className="bg-purple-600 text-white text-center">
              <CardTitle className="text-2xl">
                <Building className="inline-block mr-2 h-6 w-6" />
                Service Provider Application
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company/Business Name *
                    </label>
                    <Input
                      required
                      value={formData.companyName}
                      onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                      placeholder="Your business name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Person *
                    </label>
                    <Input
                      required
                      value={formData.contactPerson}
                      onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                      placeholder="Primary contact name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="business@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <Input
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Location *
                    </label>
                    <Input
                      required
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="City, State/Region, Country"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Years of Experience *
                    </label>
                    <Select value={formData.experience} onValueChange={(value) => setFormData({...formData, experience: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select experience" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-2">1-2 Years</SelectItem>
                        <SelectItem value="3-5">3-5 Years</SelectItem>
                        <SelectItem value="6-10">6-10 Years</SelectItem>
                        <SelectItem value="10+">10+ Years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Services Offered * (Select all that apply)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      'Deep Cleaning', 'Maintenance Cleaning', 'Laundry Services', 'Plumbing',
                      'Electrical', 'HVAC', 'General Repairs', 'Appliance Repair',
                      'Airport Transfers', 'Delivery Services', 'Photography', 'Content Creation'
                    ].map((service) => (
                      <div key={service} className="flex items-center space-x-2">
                        <Checkbox
                          id={service}
                          checked={formData.serviceTypes.includes(service)}
                          onCheckedChange={(checked) => handleServiceTypeChange(service, checked)}
                        />
                        <label htmlFor={service} className="text-sm text-gray-700">
                          {service}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Availability *
                  </label>
                  <Select value={formData.availability} onValueChange={(value) => setFormData({...formData, availability: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select availability" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekdays">Weekdays Only</SelectItem>
                      <SelectItem value="weekends">Weekends Only</SelectItem>
                      <SelectItem value="full-time">Full Time (7 days)</SelectItem>
                      <SelectItem value="part-time">Part Time (Flexible)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Description *
                  </label>
                  <Textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Tell us about your business, team size, equipment, and what makes you unique..."
                    rows={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certifications & Licenses
                  </label>
                  <Textarea
                    value={formData.certifications}
                    onChange={(e) => setFormData({...formData, certifications: e.target.value})}
                    placeholder="List any relevant certifications, licenses, or professional qualifications..."
                    rows={3}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="insurance"
                    checked={formData.insurance}
                    onCheckedChange={(checked) => setFormData({...formData, insurance: checked})}
                  />
                  <label htmlFor="insurance" className="text-sm text-gray-700">
                    I confirm that my business has general liability insurance coverage *
                  </label>
                </div>

                <Button type="submit" size="lg" className="w-full bg-purple-600 hover:bg-purple-700">
                  Submit Application
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Our team will review your application and contact you within 48 hours.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ServiceProviders;